// Native push registration via @react-native-firebase/messaging, behind a
// guarded require so the app never crashes when the module isn't in the build
// yet (same pattern as lib/auth). Until the dependency is installed + the app
// rebuilt, isAvailable() is false and requestPermissionAndToken() returns null.
// Web is a no-op (index.web.ts) — FCM web needs a service worker + VAPID key,
// deferred.

import { Platform } from 'react-native';

/* eslint-disable @typescript-eslint/no-explicit-any */
let mod: any | null = null;
let attempted = false;

const getMessaging = (): any | null => {
  if (Platform.OS === 'web') return null;
  if (attempted) return mod;
  attempted = true;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    mod = require('@react-native-firebase/messaging').default;
  } catch {
    mod = null;
  }
  return mod;
};

let firestoreMod: any | null = null;
let fsAttempted = false;
const getFirestore = (): any | null => {
  if (Platform.OS === 'web') return null;
  if (fsAttempted) return firestoreMod;
  fsAttempted = true;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    firestoreMod = require('@react-native-firebase/firestore').default;
  } catch {
    firestoreMod = null;
  }
  return firestoreMod;
};

export const isAvailable = (): boolean => getMessaging() !== null;

// Ask for notification permission and return the device FCM token (or null if
// unavailable / denied). Store the returned token in Firestore so the
// scheduled function can target this device.
export const requestPermissionAndToken = async (): Promise<string | null> => {
  const m = getMessaging();
  if (!m) return null;
  try {
    const messaging = m();
    const status = await messaging.requestPermission();
    const enabled =
      status === m.AuthorizationStatus.AUTHORIZED || status === m.AuthorizationStatus.PROVISIONAL;
    if (!enabled) return null;
    return await messaging.getToken();
  } catch {
    return null;
  }
};

export const onTokenRefresh = (cb: (token: string) => void): (() => void) => {
  const m = getMessaging();
  if (!m) return () => {};
  return m().onTokenRefresh(cb);
};

// Store the device token under users/{uid}.fcmTokens so the scheduled function
// can target it. No-ops when Firestore / a uid isn't available.
export const saveFcmToken = async (uid: string, token: string): Promise<void> => {
  const fs = getFirestore();
  if (!fs || !uid || !token) return;
  try {
    await fs()
      .collection('users')
      .doc(uid)
      .set({ fcmTokens: fs.FieldValue.arrayUnion(token) }, { merge: true });
  } catch {
    /* ignore — token registration is best-effort */
  }
};

// Mirror the user's alerts to users/{uid}.alerts so the scheduled function can
// match new jobs against them. One-way (local → Firestore); no-ops without a
// uid / Firestore.
export const saveAlerts = async (uid: string, alerts: unknown[]): Promise<void> => {
  const fs = getFirestore();
  if (!fs || !uid) return;
  try {
    await fs().collection('users').doc(uid).set({ alerts }, { merge: true });
  } catch {
    /* ignore — best-effort */
  }
};

// One call to wire push for a user: ask permission, get the token, store it.
// Returns the token (or null if unavailable / denied). Call it when the user
// shows intent — e.g. creates their first alert.
export const registerForPush = async (
  uid: string | null | undefined
): Promise<string | null> => {
  if (!uid) return null;
  const token = await requestPermissionAndToken();
  if (token) await saveFcmToken(uid, token);
  return token;
};

export default {
  isAvailable,
  requestPermissionAndToken,
  onTokenRefresh,
  saveFcmToken,
  saveAlerts,
  registerForPush,
};
